<template>
  <div class="cm-cierre-odt">
    <v-container fluid>
      <v-row dense>
        <v-col cols="12">
          <h3 class="text-subtitle-1 font-weight-bold mb-1">Cierre de ODT</h3>
          <p class="text-caption mb-0">
            Ejecuta el cierre automático de ODT. Los filtros de fecha son <strong>opcionales</strong>: delimitan el
            periodo de <strong>FechaEntrega</strong> de las ODT a finalizar. Si no se indican, se usa el
            tope calculado desde los horarios activos (CAT_Horarios).
          </p>
        </v-col>
      </v-row>

      <!-- Acciones -->
      <v-row dense align="center">
        <v-col cols="12" md="3" sm="6">
          <VueCtkDateTimePicker v-model="inicio" label="Inicio (opcional)" format="YYYY-MM-DD HH:mm"
            formatted="YYYY-MM-DD HH:mm" noHeader overlay buttonNowTranslation="Ahora" />
        </v-col>

        <v-col cols="12" md="3" sm="6">
          <VueCtkDateTimePicker v-model="fin" :min-date="inicio" label="Fin (opcional)" format="YYYY-MM-DD HH:mm"
            formatted="YYYY-MM-DD HH:mm" noHeader overlay buttonNowTranslation="Ahora" />
        </v-col>

        <v-col cols="12" md="6" class="d-flex align-center flex-wrap cm-cierre-acciones">
          <v-btn color="green" dark :loading="ejecutarLoading" :disabled="estado.enEjecucion" @click="ejecutar">
            Ejecutar cierre
          </v-btn>

          <v-btn color="red" dark :loading="detenerLoading"
            :disabled="!estado.enEjecucion || estado.detencionSolicitada" @click="detener">
            Detener
          </v-btn>

          <v-btn color="primary" outlined :loading="estadoLoading" @click="obtenerEstado()">
            Refrescar estado
          </v-btn>

          <v-btn color="blue-grey" outlined :loading="descargarLogLoading" @click="descargarLog">
            Descargar log
          </v-btn>

          <v-btn color="red" outlined :loading="limpiarLogLoading" @click="limpiarLog">
            Limpiar log
          </v-btn>
        </v-col>
      </v-row>

      <v-row dense v-if="mensaje">
        <v-col cols="12">
          <v-alert dense text :type="mensajeTipo" dismissible @input="mensaje = ''">
            {{ mensaje }}
          </v-alert>
        </v-col>
      </v-row>

      <!-- Estado -->
      <v-row dense>
        <v-col cols="12">
          <v-card outlined>
            <v-card-title class="text-subtitle-1 py-2">
              Estado
              <v-spacer></v-spacer>
              <v-chip small :color="estado.enEjecucion ? 'green' : 'grey'" dark>
                {{ estado.enEjecucion ? 'En ejecución' : 'Detenido' }}
              </v-chip>
              <v-chip v-if="estado.detencionSolicitada" small color="orange" dark class="ml-2">
                Deteniéndose…
              </v-chip>
            </v-card-title>

            <v-card-text>
              <v-row dense>
                <v-col cols="12" md="4" sm="6">
                  <strong>ODT en proceso:</strong> {{ formatId(estado.odtEnProceso) }}
                </v-col>
                <v-col cols="12" md="4" sm="6">
                  <strong>Disparado por:</strong> {{ estado.disparadoPor || '—' }}
                </v-col>
                <v-col cols="12" md="4" sm="6">
                  <strong>Inicio ejecución:</strong> {{ formatFecha(estado.inicioEjecucion) }}
                </v-col>
                <v-col cols="12" md="4" sm="6">
                  <strong>Periodo inicio:</strong> {{ formatFecha(estado.periodoInicio) }}
                </v-col>
                <v-col cols="12" md="4" sm="6">
                  <strong>Periodo fin:</strong> {{ formatFecha(estado.periodoFin) }}
                </v-col>
                <v-col cols="12" md="4" sm="6">
                  <strong>Total ODT cerradas:</strong> {{ estado.totalOdtsCerradas || 0 }}
                </v-col>
              </v-row>

              <template v-if="estado.detencionSolicitada && estado.odtEnProceso != null">
                <v-alert dense text type="warning" class="mt-2 mb-0">
                  Esperando a que termine de cerrar la ODT {{ estado.odtEnProceso }} para detenerse.
                </v-alert>
              </template>

              <v-divider class="my-3"></v-divider>

              <div class="text-subtitle-2 mb-2">ODT cerradas</div>
              <div v-if="estado.odtsCerradas && estado.odtsCerradas.length" class="d-flex flex-wrap cm-cierre-chips">
                <v-chip v-for="id in estado.odtsCerradas" :key="`cerrada-${id}`" small color="green" outlined>
                  {{ id }}
                </v-chip>
              </div>
              <div v-else class="text-caption">Sin ODT cerradas en la última corrida.</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
import qs from "qs";

function createEstado() {
  return {
    enEjecucion: false,
    detencionSolicitada: false,
    odtEnProceso: null,
    inicioEjecucion: null,
    disparadoPor: null,
    periodoInicio: null,
    periodoFin: null,
    totalOdtsCerradas: 0,
    odtsCerradas: [],
  };
}

export default {
  name: "ControlMaestroCierreODT",

  data() {
    return {
      inicio: null,
      fin: null,
      estado: createEstado(),
      ejecutarLoading: false,
      detenerLoading: false,
      estadoLoading: false,
      descargarLogLoading: false,
      limpiarLogLoading: false,
      mensaje: "",
      mensajeTipo: "info",
    };
  },

  mounted() {
    this.obtenerEstado();
  },

  methods: {
    mostrarMensaje(texto, tipo = "info") {
      this.mensaje = texto;
      this.mensajeTipo = tipo;
    },

    formatId(valor) {
      return valor === null || valor === undefined ? "—" : valor;
    },

    formatFecha(valor) {
      if (!valor) return "—";
      const fecha = new Date(valor);
      if (Number.isNaN(fecha.getTime())) return String(valor);
      const pad = (n) => String(n).padStart(2, "0");
      return `${fecha.getFullYear()}-${pad(fecha.getMonth() + 1)}-${pad(fecha.getDate())} ${pad(fecha.getHours())}:${pad(fecha.getMinutes())}:${pad(fecha.getSeconds())}`;
    },

    async ejecutar() {
      this.ejecutarLoading = true;
      try {
        const { data } = await axios.post("CierreOdt/Ejecutar", null, {
          params: { inicio: this.inicio, fin: this.fin },
          paramsSerializer: (params) => qs.stringify(params, { skipNulls: true }),
        });
        this.mostrarMensaje(data?.mensaje || "Cierre de ODT iniciado.", "success");
        await this.obtenerEstado();
      } catch (error) {
        const msg = error?.response?.data?.mensaje;
        const status = error?.response?.status;
        if (status === 409) {
          this.mostrarMensaje(msg || "Ya hay un cierre de ODT en curso.", "warning");
        } else if (status === 400) {
          this.mostrarMensaje(msg || "Parámetros inválidos.", "error");
        } else {
          this.mostrarMensaje("Error al iniciar el cierre de ODT.", "error");
        }
        console.error("Error al ejecutar cierre ODT:", error);
      } finally {
        this.ejecutarLoading = false;
      }
    },

    async detener() {
      this.detenerLoading = true;
      try {
        const { data } = await axios.post("CierreOdt/Detener");
        this.mostrarMensaje(data?.mensaje || "Se solicitó detener el cierre de ODT.", "info");
        await this.obtenerEstado();
      } catch (error) {
        const msg = error?.response?.data?.mensaje;
        if (error?.response?.status === 409) {
          this.mostrarMensaje(msg || "No hay un cierre de ODT en curso.", "warning");
        } else {
          this.mostrarMensaje("Error al detener el cierre de ODT.", "error");
        }
        console.error("Error al detener cierre ODT:", error);
      } finally {
        this.detenerLoading = false;
      }
    },

    async obtenerEstado() {
      this.estadoLoading = true;
      try {
        const { data } = await axios.get("CierreOdt/Estado");
        this.estado = {
          ...createEstado(),
          ...(data || {}),
          odtsCerradas: Array.isArray(data?.odtsCerradas) ? data.odtsCerradas : [],
        };
      } catch (error) {
        this.mostrarMensaje("Error al consultar el estado del cierre de ODT.", "error");
        console.error("Error al obtener estado cierre ODT:", error);
      } finally {
        this.estadoLoading = false;
      }
    },

    async descargarLog() {
      this.descargarLogLoading = true;
      try {
        const { data } = await axios.get("CierreOdt/DescargarLog", {
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "cierre_odt.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        if (error?.response?.status === 404) {
          this.mostrarMensaje("No hay log de cierre de ODT.", "warning");
        } else {
          this.mostrarMensaje("Error al descargar el log de cierre de ODT.", "error");
        }
        console.error("Error al descargar log cierre ODT:", error);
      } finally {
        this.descargarLogLoading = false;
      }
    },

    async limpiarLog() {
      this.limpiarLogLoading = true;
      try {
        const { data } = await axios.post("CierreOdt/LimpiarLog");
        this.mostrarMensaje(data?.mensaje || "Log de cierre de ODT eliminado.", "success");
      } catch (error) {
        const msg = error?.response?.data?.mensaje;
        if (error?.response?.status === 409) {
          this.mostrarMensaje(msg || "No hay log de cierre de ODT para limpiar.", "warning");
        } else {
          this.mostrarMensaje("Error al limpiar el log de cierre de ODT.", "error");
        }
        console.error("Error al limpiar log cierre ODT:", error);
      } finally {
        this.limpiarLogLoading = false;
      }
    },

    reset() {
      this.inicio = null;
      this.fin = null;
      this.mensaje = "";
      this.obtenerEstado();
    },
  },
};
</script>

<style scoped>
.cm-cierre-acciones {
  gap: 8px;
}

.cm-cierre-chips {
  gap: 4px;
}
</style>
